import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, CalendarIcon, TrashIcon } from '@heroicons/react/24/outline';
import { TaskService } from '../../services/TaskService';
import { STATUS_OPTIONS } from '../../utils/statusConfig';
import toast from 'react-hot-toast';
import { formatDateForAPI, formatDateForInput, isEndDateAfterStartDate, getMaxDateString } from '../../utils/dateUtils';

const EditTaskModal = ({ isOpen, onClose, task, onTaskUpdated }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'pending',
    initialDate: '',
    finalDate: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (task && isOpen) {
      setFormData({
        name: task.name || '',
        description: task.description || '',
        status: task.status || 'pending',
        initialDate: formatDateForInput(task.initialDate),
        finalDate: formatDateForInput(task.finalDate)
      });
    }
  }, [task, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.description || !formData.initialDate || !formData.finalDate) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    if (!isEndDateAfterStartDate(formData.initialDate, formData.finalDate)) {
      toast.error('La fecha de fin debe ser posterior a la fecha de inicio');
      return;
    }
    
    setLoading(true);
    
    try {
      const taskData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        status: formData.status,
        initialDate: formatDateForAPI(formData.initialDate),
        finalDate: formatDateForAPI(formData.finalDate)
      };
      
      const result = await TaskService.updateTask(task.id, taskData);
      
      if (result.success) {
        toast.success('Tarea actualizada exitosamente');
        if (onTaskUpdated) {
          onTaskUpdated();
        }
        handleClose();
      } else {
        toast.error(result.error || 'Error al actualizar la tarea');
      }
    } catch (error) {
      toast.error('Error al actualizar la tarea');
      console.error('Error updating task:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      status: 'pending',
      initialDate: '',
      finalDate: ''
    });
    onClose();
  };

  const handleDelete = async () => {
    // Confirmar eliminación
    const confirmed = window.confirm(`¿Estás seguro de que deseas eliminar la tarea "${task?.name}"?`);
    if (!confirmed) return;

    const loadingToast = toast.loading('Eliminando tarea...');
    setLoading(true);
    
    try {
      const result = await TaskService.deleteTask(task.id);
      
      if (result.success) {
        toast.success('Tarea eliminada exitosamente', { id: loadingToast });
        if (onTaskUpdated) {
          onTaskUpdated();
        }
        handleClose();
      } else {
        toast.error(result.error || 'Error al eliminar la tarea', { id: loadingToast });
      }
    } catch (error) {
      toast.error('Error al eliminar la tarea', { id: loadingToast });
      console.error('Error deleting task:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[70] overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50"
              onClick={handleClose}
            />
            
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-lg bg-white dark:bg-gray-800 rounded-xl shadow-xl"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Editar Tarea
                </h2>
                <button
                  onClick={handleClose}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <XMarkIcon className="h-5 w-5 text-gray-500" />
                </button>
              </div>
              
              {/* Form */}
              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                {/* Nombre */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Nombre de la Tarea *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="input-field w-full"
                    placeholder="Ej: Pruebas Cucumber"
                    required
                  />
                </div>

                {/* Descripción */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Descripción *
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    className="input-field w-full"
                    placeholder="Descripción detallada de la tarea"
                    rows="3"
                    required
                  />
                </div>

                {/* Estado */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Estado *
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="input-field w-full"
                    required
                  >
                    {STATUS_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Fecha Inicial */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Fecha de Inicio *
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      name="initialDate"
                      value={formData.initialDate}
                      onChange={handleChange}
                      max={getMaxDateString()}
                      className="input-field w-full pr-10"
                      required
                    />
                    <CalendarIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                  </div>
                </div>

                {/* Fecha Final */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Fecha de Fin *
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      name="finalDate"
                      value={formData.finalDate}
                      onChange={handleChange}
                      min={formData.initialDate}
                      max={getMaxDateString()}
                      className="input-field w-full pr-10"
                      required
                    />
                    <CalendarIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                  </div>
                </div>

                {/* Botones */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                  {/* Botón de eliminar a la izquierda */}
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={loading}
                    className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors flex items-center space-x-2"
                  >
                    <TrashIcon className="h-4 w-4" />
                    <span>Eliminar</span>
                  </button>
                  
                  {/* Botones de cancelar y actualizar a la derecha */}
                  <div className="flex items-center space-x-3">
                    <button
                      type="button"
                      onClick={handleClose}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="btn-primary flex items-center space-x-2"
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span>Actualizando...</span>
                        </>
                      ) : (
                        <span>Actualizar Tarea</span>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default EditTaskModal;

